import React, { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { earnDetailsBreadcrumbAtom } from './atoms/breadcrumbAtom'
import { EarnAssetDetailsHeader } from './components/EarnAssetDetailsHeader'
import { EarnAssetEarningsSummary } from './components/EarnAssetEarningsSummary'
import { EarnAssetFundsSummary } from './components/EarnAssetFundsSummary'
import { EarnAssetHistory } from './components/EarnAssetHistory'
import { EarnAssetSummaryDirect } from './components/EarnAssetSummaryDirect'
import { EarnSheetProvider, useEarnSheetContext } from './context/EarnSheetProvider'
import { useEarnAssetDetailsQuery } from './hooks/api/useEarnAssetDetailsQuery'
import { useEarnTracking } from './hooks/useEarnTracking'
import { useIsDirectEarning } from './hooks/useIsDirectEarning'
import { EARN_SHEET_ACTION_TYPE } from './types'
import { formatAPY, formatUsd } from './utils/utils'

export const EarnAssetDetails = () => (
  <EarnSheetProvider>
    <EarnAssetDetailsContent />
  </EarnSheetProvider>
)

const EarnAssetDetailsContent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const setEarnDetailsBreadcrumb = useSetAtom(earnDetailsBreadcrumbAtom)
  const isDirectEarning = useIsDirectEarning()
  const { handleSheetOpen } = useEarnSheetContext()
  const { trackPageView } = useEarnTracking()

  const { data, isLoading } = useEarnAssetDetailsQuery(portfolioId)

  useEffect(() => {
    setEarnDetailsBreadcrumb(data?.asset ?? '')
    return () => setEarnDetailsBreadcrumb('')
  }, [data?.asset, setEarnDetailsBreadcrumb])

  useEffect(() => {
    trackPageView('details', { portfolioId })
  }, [trackPageView, portfolioId])

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (!data) {
    navigate(PathNames.NOT_FOUND)
    return null
  }

  const earnRate = formatAPY(data.myEarnRate ?? 0)

  const handleDeposit = () => {
    handleSheetOpen({
      type: EARN_SHEET_ACTION_TYPE.DEPOSIT,
      header: t('earn.depositSheet.header'),
      selectedCurrency: data.asset,
      portfolioId,
      productName: data.productName,
    })
  }

  const handleWithdraw = () => {
    handleSheetOpen({
      type: EARN_SHEET_ACTION_TYPE.WITHDRAW,
      header: t('earn.withdrawSheet.header'),
      selectedCurrency: data.asset,
      portfolioId,
      productName: data.productName,
    })
  }

  return (
    <>
      <EarnAssetDetailsHeader
        asset={data.asset}
        earnRate={earnRate}
        onWithdrawClick={handleWithdraw}
        onDepositClick={handleDeposit}
      />
      <Box flexCol gap={24}>
        {isDirectEarning ? (
          <EarnAssetSummaryDirect
            asset={data.asset}
            earnRate={earnRate}
            allocatedFunds={formatUsd(data.fundsOverview.allocatedFunds)}
            myLifetimeEarnings={formatUsd(data.earningsSummary.myLifetimeEarnings)}
          />
        ) : (
          <>
            <EarnAssetFundsSummary
              asset={data.asset}
              earnRate={earnRate}
              allocatedFunds={formatUsd(data.fundsOverview.allocatedFunds)}
              totalCustomerEligibleFunds={formatUsd(data.fundsOverview.totalCustomerEligibleFunds)}
            />

            <EarnAssetEarningsSummary
              myLifetimeEarnings={formatUsd(data.earningsSummary.myLifetimeEarnings)}
              customerEarningsReceived={formatUsd(data.earningsSummary.customerEarningsReceived)}
              distributedEarningsToCustomers={formatUsd(
                data.earningsSummary.distributedEarningsToCustomers
              )}
            />
          </>
        )}

        <EarnAssetHistory portfolioId={portfolioId} walletId={data.walletId} />
      </Box>
    </>
  )
}
